/**
 * Strings.
 *
 * English and Puerto Rican Spanish are both first-class from the first commit.
 * Retrofitting i18n means finding every string already inlined in a component,
 * and on this account that has meant a translation layer that only ever covered
 * the pages someone remembered.
 *
 * `es-PR`, not generic `es`: the wording is Puerto Rican, and the distinction
 * matters to the people reading it.
 */

export type Locale = 'en' | 'es-PR'

const STRINGS = {
  en: {
    'app.signIn': 'Sign in',
    'app.signOut': 'Sign out',
    'app.loading': 'Loading…',
    'app.brief': 'Closing brief',

    'cc.today': 'Today',
    'cc.funding': 'Funding',
    'cc.debt': 'Debt plan',
    'cc.file': 'The file',
    'cc.conflict': 'Data conflict — do not act from a single tile',
    'cc.pull': 'Pull from 42payments',
    'cc.pulling': 'Pulling…',
    'cc.ask': 'Summon GoG',
    'gog.title': 'Gasp of God',
    'gog.close': 'Close',
    'gog.mic': 'Dictate',
    'gog.stop': 'Stop listening',
    'gog.listening': 'listening…',
    'gog.build': 'Build a command',
    'gog.buildHide': 'Hide the keys',
    'cc.decisionsOpen': 'decisions open',

    'auth.handle': 'Username',
    'auth.displayName': 'Your name',
    'auth.createAccount': 'Create account',
    'auth.usePasskey': 'Sign in with a passkey',
    'auth.noPasskeySupport': 'This browser cannot use passkeys. Try Chrome, Safari or Edge.',
    'auth.setupToken': 'Setup token',
    'auth.cancelled': 'That was cancelled. Nothing has changed.',

    'account.passkeys': 'Passkeys',
    'account.addPasskey': 'Add a passkey',
    'account.removePasskey': 'Remove',
    'account.synced': 'Synced to your password manager',
    'account.deviceOnly': 'This device only',
    'account.neverUsed': 'Never used',
    'account.lastUsed': 'Last used',
    'account.recoveryWarning':
      'This account has one passkey and it is not synced. Lose that device and the account cannot be recovered — add a second passkey.',

    'notify.enable': 'Turn on notifications',
    'notify.enabled': 'Notifications are on',
    'notify.blocked': 'Notifications are blocked in your browser settings.',
    'notify.inbox': 'Notifications',
    'notify.empty': 'Nothing yet.',

    'admin.title': 'Admin',
    'admin.users': 'Users',
    'admin.audit': 'Activity',
    'admin.errors': 'Errors',
    'admin.flags': 'Flags',
    'admin.settings': 'Settings',
    'admin.broadcast': 'Broadcast',
    'admin.devices': 'Devices',

    'env.beta': 'BETA — this is not the live app',
    'env.local': 'LOCAL',

    'error.generic': 'Something went wrong.',
    'error.offline': 'You appear to be offline.',

    'update.ready': 'A newer version is ready',
    'update.now': 'Update now',
    'update.later': 'Later',

    'install.title': 'Put this on your home screen',
    'install.why': 'It gets its own icon and opens without the browser bar.',
    'install.ios': 'Tap Share, then “Add to Home Screen”.',
    'install.add': 'Add it',
    'install.no': 'No thanks',
  },

  'es-PR': {
    'app.signIn': 'Entrar',
    'app.signOut': 'Salir',
    'app.loading': 'Cargando…',
    'app.brief': 'Resumen de cierre',

    'cc.today': 'Hoy',
    'cc.funding': 'Financiamiento',
    'cc.debt': 'Plan de deuda',
    'cc.file': 'El expediente',
    'cc.conflict': 'Datos en conflicto — no actúes por un solo dato',
    'cc.pull': 'Traer de 42payments',
    'cc.pulling': 'Trayendo…',
    'cc.ask': 'Invocar a GoG',
    'gog.title': 'Gasp of God',
    'gog.close': 'Cerrar',
    'gog.mic': 'Dictar',
    'gog.stop': 'Dejar de escuchar',
    'gog.listening': 'escuchando…',
    'gog.build': 'Armar un comando',
    'gog.buildHide': 'Ocultar las teclas',
    'cc.decisionsOpen': 'decisiones abiertas',

    'auth.handle': 'Usuario',
    'auth.displayName': 'Tu nombre',
    'auth.createAccount': 'Crear cuenta',
    'auth.usePasskey': 'Entrar con passkey',
    'auth.noPasskeySupport': 'Este navegador no puede usar passkeys. Prueba Chrome, Safari o Edge.',
    'auth.setupToken': 'Código de configuración',
    'auth.cancelled': 'Se canceló. No cambió nada.',

    'account.passkeys': 'Passkeys',
    'account.addPasskey': 'Añadir un passkey',
    'account.removePasskey': 'Quitar',
    'account.synced': 'Sincronizado con tu gestor de contraseñas',
    'account.deviceOnly': 'Solo en este equipo',
    'account.neverUsed': 'Nunca se ha usado',
    'account.lastUsed': 'Último uso',
    'account.recoveryWarning':
      'Esta cuenta tiene un solo passkey y no está sincronizado. Si pierdes ese equipo, no se puede recuperar la cuenta — añade un segundo passkey.',

    'notify.enable': 'Activar notificaciones',
    'notify.enabled': 'Las notificaciones están activadas',
    'notify.blocked': 'Las notificaciones están bloqueadas en tu navegador.',
    'notify.inbox': 'Notificaciones',
    'notify.empty': 'Nada todavía.',

    'admin.title': 'Administración',
    'admin.users': 'Usuarios',
    'admin.audit': 'Actividad',
    'admin.errors': 'Errores',
    'admin.flags': 'Flags',
    'admin.settings': 'Ajustes',
    'admin.broadcast': 'Mensaje',
    'admin.devices': 'Equipos',

    'env.beta': 'BETA — esta no es la app en vivo',
    'env.local': 'LOCAL',

    'error.generic': 'Algo salió mal.',
    'error.offline': 'Parece que no tienes conexión.',

    'update.ready': 'Hay una versión más nueva',
    'update.now': 'Actualizar',
    'update.later': 'Ahora no',

    'install.title': 'Ponlo en tu pantalla de inicio',
    'install.why': 'Tiene su propio ícono y abre sin la barra del navegador.',
    'install.ios': 'Toca Compartir y luego “Añadir a inicio”.',
    'install.add': 'Añadir',
    'install.no': 'Ahora no',
  },
} as const

export type StringKey = keyof (typeof STRINGS)['en']

let locale: Locale = detect()

function detect(): Locale {
  const stored = localStorage.getItem('locale')
  if (stored === 'en' || stored === 'es-PR') return stored
  return navigator.language.toLowerCase().startsWith('es') ? 'es-PR' : 'en'
}

export function getLocale(): Locale {
  return locale
}

export function setLocale(next: Locale): void {
  locale = next
  localStorage.setItem('locale', next)
  document.documentElement.lang = next
}

/**
 * A missing translation falls back to English rather than rendering the key.
 * A user seeing `account.passkeys` learns nothing; a user seeing the English
 * learns what the control does.
 */
export function t(key: StringKey): string {
  return STRINGS[locale][key] ?? STRINGS.en[key] ?? key
}
